import { memo, useCallback, useState, useRef } from 'react';
import { memoizedSequence } from 'UIComponents/core/Functions';
import { MSG_TYPE_RESIZE_MINI_COMMENTER, MSG_TYPE_RESIZED_MINI_COMMENTER, MSG_TYPE_COMMENTS_BROADCAST, MSG_TYPE_CLOSE_MINI_COMMENTER, MSG_TYPE_SET_MINI_COMMENTER_MIN_HEIGHT_FOR_POPOVER, MSG_TYPE_MINI_COMMENTER_LOADED_COMMENTS, MSG_TYPE_UPDATE_COLLABORATION_SIDEBAR_STATE } from 'collaboration-sidebar/constants/IFrameMessageTypes';
import { IFRAME_INIT_TIMEOUT } from 'collaboration-sidebar/constants/iframe';
import useHandleIFrameError from 'collaboration-sidebar/hooks/useHandleIFrameError';
import useCommentIndicatorsSync from 'collaboration-sidebar/features/deepCommenting/hooks/useCommentIndicatorsSync';
import useShowNotifications from 'collaboration-sidebar/hooks/useShowNotifications';
import { getEmbeddableMiniCommenterUrl } from 'collaboration-sidebar/utils/urls';
import AsyncIFrame from 'collaboration-sidebar/components/AsyncUIIFrame';
import MiniCommenterSkeleton from 'collaboration-sidebar/components/MiniCommenterSkeleton';
import useMiniCommenterSize from '../hooks/useMiniCommenterSize';
import useEmbeddedComments from '../hooks/useEmbeddedComments';
import useEmbeddedCommentsMessaging from '../hooks/useEmbeddedCommentsMessaging';
import useCollaborationSidebarState from '../../../hooks/useCollaborationSidebarState';
import { useUserInfo } from 'collaboration-sidebar-common/userInfo/userInfoQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function EmbeddedCommentIframe({
  onRequestClose,
  isOpen,
  autoFocusCommenter = true,
  embeddedModuleID,
  embeddedMetaDescription
}) {
  'use memo';

  const sendMessageRef = useRef(undefined);
  const {
    userInfo
  } = useUserInfo();
  const {
    onUpdateCollaborationSidebarState,
    collaborationSidebarState
  } = useCollaborationSidebarState();
  const {
    setModuleReply,
    removeModuleReply,
    broadcastCommentsMessage,
    moduleParentComment,
    incrementReplyCountBy
  } = useEmbeddedComments({
    moduleId: embeddedModuleID || null,
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  });
  const {
    handleReady,
    broadcastDelete
  } = useEmbeddedCommentsMessaging({
    moduleId: embeddedModuleID,
    broadcastCommentsMessage,
    collaborationSidebarState,
    isOpen,
    onClose: onRequestClose
  });
  const {
    miniCommenterSize,
    baseMiniCommenterSize,
    onResizeMiniCommenter,
    setAdditionalHeightForPopover
  } = useMiniCommenterSize();
  const [isReady, setIsReady] = useState(false);
  const handleShowNotificationsMessage = useShowNotifications(true, sendMessageRef);
  const {
    objectType,
    objectId,
    ctaUrl,
    appName
  } = collaborationSidebarState;
  const {
    handleSidebarInitError,
    handleSidebarReadyError
  } = useHandleIFrameError({
    appName,
    context: 'mini-commenter'
  });
  const handleSetHeightForPopoverMessage = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_SET_MINI_COMMENTER_MIN_HEIGHT_FOR_POPOVER) {
      const shouldAddHeight = payload.threshold === undefined || baseMiniCommenterSize < payload.threshold;
      setAdditionalHeightForPopover(shouldAddHeight ? payload.height : 0);
    }
  }, [setAdditionalHeightForPopover, baseMiniCommenterSize]);
  const handleMiniCommenterLoadedComments = useCallback(({
    payload: payload_0
  }) => {
    if (payload_0.type === MSG_TYPE_MINI_COMMENTER_LOADED_COMMENTS) {
      setIsReady(true);
    }
  }, []);

  // Let the mini commenter load and report the true size of the comment list,
  // then we'll resize the iframe to fit it.
  const handleResizeIframeMessage = useCallback(({
    payload: payload_1
  }) => {
    if (payload_1.type === MSG_TYPE_RESIZE_MINI_COMMENTER) {
      onResizeMiniCommenter(payload_1.height);
      if (sendMessageRef.current) {
        sendMessageRef.current(MSG_TYPE_RESIZED_MINI_COMMENTER);
      }
    }
  }, [onResizeMiniCommenter]);
  const handleCloseMessage = useCallback(({
    payload: payload_2
  }) => {
    if (payload_2.type === MSG_TYPE_CLOSE_MINI_COMMENTER) {
      onRequestClose();
    }
  }, [onRequestClose]);
  const handleCommentsBroadcastMessage = useCallback(({
    payload: payload_3
  }) => {
    if (payload_3.type === MSG_TYPE_COMMENTS_BROADCAST) {
      broadcastCommentsMessage(payload_3.broadcastPayload);
    }
  }, [broadcastCommentsMessage]);
  const handleUpdateCollaborationSidebarState = useCallback(({
    payload: payload_4
  }) => {
    if (payload_4.type === MSG_TYPE_UPDATE_COLLABORATION_SIDEBAR_STATE) {
      onUpdateCollaborationSidebarState(payload_4.updates);
    }
  }, [onUpdateCollaborationSidebarState]);
  const {
    handleMessage: handleCommentMessage
  } = useCommentIndicatorsSync({
    setModuleReply,
    removeModuleReply,
    incrementReplyCountBy
  });
  const onMessage = memoizedSequence(handleResizeIframeMessage, handleMiniCommenterLoadedComments, handleSetHeightForPopoverMessage, broadcastDelete, handleShowNotificationsMessage, handleCommentMessage, handleCommentsBroadcastMessage, handleUpdateCollaborationSidebarState, handleCloseMessage);
  return /*#__PURE__*/_jsxs("div", {
    children: [!isReady && /*#__PURE__*/_jsx(MiniCommenterSkeleton, {
      hasModuleParentComment: !!moduleParentComment
    }), /*#__PURE__*/_jsx(AsyncIFrame, {
      initTimeout: IFRAME_INIT_TIMEOUT,
      name: "mini-commenter",
      onInitError: handleSidebarInitError,
      onReady: ({
        sendMessage
      }) => {
        handleReady({
          sendMessage
        });
        sendMessageRef.current = sendMessage;
      },
      iframePassthruProps: {
        'data-test-id': 'mini-commenter-embedded-iframe',
        'data-iframe-ready': isReady,
        style: {
          height: `${isReady ? miniCommenterSize : 0}px`,
          width: '100%'
        }
      },
      embeddedPassthruProps: {
        metaDescription: embeddedMetaDescription,
        moduleParentComment,
        collaborationSidebarState,
        userInfo
      },
      onReadyError: handleSidebarReadyError,
      onMessage: onMessage,
      src: getEmbeddableMiniCommenterUrl({
        objectType,
        objectId,
        ctaUrl,
        appName,
        moduleId: embeddedModuleID,
        autoFocus: autoFocusCommenter,
        parentCommentId: moduleParentComment === null || moduleParentComment === void 0 ? void 0 : moduleParentComment.id
      })
    })]
  });
}
export default /*#__PURE__*/memo(EmbeddedCommentIframe);